import { useState, useEffect, useContext } from "react";
import axios from "axios";
import {
    Card,
    Spin,
    Input,
    Pagination,
    Select,
    DatePicker,
} from "antd";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
} from "recharts";
import { motion } from "framer-motion";
import { AuthContext } from "../../context/AuthContext";
import dayjs from "dayjs";

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

export default function EmployeeAttendance() {
    const { authData, backUrl } = useContext(AuthContext) || { backUrl: "" };

    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);

    const [organisations, setOrganisations] = useState([]);
    const [organisationId, setOrganisationId] = useState(1);

    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    const [departmentId, setDepartmentId] = useState("");
    const [designationId, setDesignationId] = useState("");

    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);

    const pageSize = 10;

    // Toggle for chart type
    const [chartType, setChartType] = useState("bar");

    // Default date range = today
    const [dates, setDates] = useState([
        dayjs().format("YYYY-MM-DD"),
        dayjs().format("YYYY-MM-DD"),
    ]);

    // Fetch Organisation dropdown
    const fetchOrganisation = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setOrganisations(res.data.results || []);
        } catch (err) {
            console.log("Error fetching organisations:", err);
        }
    };

    // Fetch departments based on organisation
    const fetchDepartments = async (orgId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/departments/?organisation=${orgId}`,
                {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                }
            );
            setDepartments(res.data.results || []);
        } catch (err) {
            console.log("Error fetching departments:", err);
        }
    };

    // Fetch designations based on organisation
    const fetchDesignations = async (orgId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/designations/?organisation=${orgId}`,
                {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                }
            );
            setDesignations(res.data.results || []);
        } catch (err) {
            console.log("Error fetching designations:", err);
        }
    };

    useEffect(() => {
        fetchOrganisation();
    }, []);

    // When organisation changes → reload departments & designations
    useEffect(() => {
        if (organisationId) {
            fetchDepartments(organisationId);
            fetchDesignations(organisationId);
            setDepartmentId("");
            setDesignationId("");
        }
    }, [organisationId]);

    // Fetch attendance summary
    const fetchAttendance = async () => {
        setLoading(true);
        try {
            const res = await axios.get(
                `${backUrl}/api/hr/employee-attendance-summary/?from_date=${dates[0]}&to_date=${dates[1]}&organisation=${organisationId}&department=${departmentId}&designation=${designationId}`,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                    },
                }
            );

            const processed = res.data.map((emp) => {
                let present = 0;
                let absent = 0;

                emp.attendance.forEach((a) => {
                    if (a.status === "PRESENT") present++;
                    if (a.status === "ABSENT") absent++;
                });

                return {
                    employee_name: emp.employee_name,
                    employee_code: emp.employee_code,
                    present,
                    absent,
                };
            });

            setData(processed);
            setFilteredData(processed);
        } catch (err) {
            console.log("Error:", err);
        } finally {
            setLoading(false);
        }
    };

    // Fetch on filters change
    useEffect(() => {
        fetchAttendance();
    }, [organisationId, departmentId, designationId, dates]);

    // Search Filter
    const handleSearch = (value) => {
        const lower = value.toLowerCase();
        const results = data.filter((d) =>
            d.employee_name.toLowerCase().includes(lower)
        );
        setFilteredData(results);
        setCurrentPage(1);
    };

    // Paginate
    const paginatedData = filteredData.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    return (
        <div className="p-6 bg-gray-50 min-h-screen flex flex-col items-center">

            {/* FILTERS */}
            <Card className="w-full max-w-6xl shadow-sm rounded-2xl mb-8">

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">

                    {/* Organisation */}
                    <Select
                        value={organisationId}
                        onChange={(v) => setOrganisationId(v)}
                        style={{ width: "30%", marginRight: '5px' }}
                    >
                        {organisations.map((o) => (
                            <Option key={o.id} value={o.id}>
                                {o.name}
                            </Option>
                        ))}
                    </Select>

                    {/* Department */}
                    <Select
                        placeholder="Select Department"
                        value={departmentId}
                        onChange={(v) => setDepartmentId(v)}
                        allowClear
                        style={{ width: "30%", marginRight: '5px' }}
                    >
                        {departments.map((d) => (
                            <Option key={d.id} value={d.id}>
                                {d.name}
                            </Option>
                        ))}
                    </Select>

                    {/* Designation */}
                    <Select
                        placeholder="Select Designation"
                        value={designationId}
                        onChange={(v) => setDesignationId(v)}
                        allowClear
                        style={{ width: "30%", marginRight: '2px' }}
                    >
                        {designations.map((d) => (
                            <Option key={d.id} value={d.id}>
                                {d.name}
                            </Option>
                        ))}
                    </Select>

                    {/* Date Range */}
                    <RangePicker
                        allowClear={false}
                        value={[dayjs(dates[0]), dayjs(dates[1])]}
                        onChange={(val) => {
                            if (val) {
                                setDates([
                                    val[0].format("YYYY-MM-DD"),
                                    val[1].format("YYYY-MM-DD"),
                                ]);
                            }
                        }}
                        style={{ width: "30%", marginTop: '5px' }}
                    />
                    <Search
                        placeholder="Search employee..."
                        allowClear
                        onSearch={handleSearch}
                        style={{ width: 200, marginLeft: '5px',  marginTop: '5px' }}
                    />

                    {/* Chart toggle */}
                    <button
                        style={{ marginTop: '5px', width: 200 }}

                        onClick={() => setChartType(chartType === "bar" ? "pie" : "bar")}
                        className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition"
                    >
                        {chartType === "bar" ? "Pie Chart" : "Bar Chart"}
                    </button>
                </div>

                {/* Search & Toggle */}
                <div className="mt-4 flex justify-end gap-3">


                </div>
            </Card>

            {/* CHART */}
            <Card className="w-full max-w-6xl shadow-md rounded-2xl">

                {loading ? (
                    <div className="flex justify-center items-center h-96">
                        <Spin size="large" />
                    </div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        {paginatedData.length === 0 ? (
                            <p className="text-center text-gray-500 py-16 text-lg">
                                No records found.
                            </p>
                        ) : chartType === "bar" ? (

                            /* BAR CHART */
                            <ResponsiveContainer width="100%" height={360}>
                                <BarChart
                                    data={paginatedData}
                                    margin={{ top: 20, right: 30, left: 0, bottom: 60 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="employee_name"
                                        angle={-45}
                                        textAnchor="end"
                                        interval={0}
                                        height={80}
                                        tick={{ fontSize: 12 }}
                                    />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="present" fill="#00AF50" name="Present" />
                                    <Bar dataKey="absent" fill="#FE0000" name="Absent" />
                                </BarChart>
                            </ResponsiveContainer>

                        ) : (

                            /* PIE CHART */
                            <ResponsiveContainer width="100%" height={360}>
                                <PieChart>
                                    <Pie
                                        data={[
                                            {
                                                name: "Total Present",
                                                value: paginatedData.reduce(
                                                    (sum, e) => sum + e.present,
                                                    0
                                                ),
                                            },
                                            {
                                                name: "Total Absent",
                                                value: paginatedData.reduce(
                                                    (sum, e) => sum + e.absent,
                                                    0
                                                ),
                                            },
                                        ]}
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={120}
                                        label
                                    >
                                        <Cell fill="#00AF50" />
                                        <Cell fill="#FE0000" />
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>

                        )}
                    </motion.div>
                )}
            </Card>

            {/* PAGINATION */}
            {filteredData.length > pageSize && (
                <div className="mt-6">
                    <Pagination
                        current={currentPage}
                        pageSize={pageSize}
                        total={filteredData.length}
                        onChange={(page) => setCurrentPage(page)}
                        showSizeChanger={false}
                    />
                </div>
            )}
        </div>
    );
}
